import { AlertService } from '@app/core';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  type SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { AlertItem, alertTarget } from '@app/model';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { IncrementalRows } from '../table/load-more.directive';

export type AlertTab = 'all' | 'company' | 'list';

/**
 * The alerts of the user with tabs by kind and an unread filter. Opening an
 * alert does not mark it read; each alert has its own button for that, and
 * "Mark all as read" marks the visible ones. The list renders 50 alerts and
 * 50 more whenever its end comes into view.
 */
@Component({
  selector: 'app-alert-list',
  templateUrl: './alert-list.component.html',
  styleUrls: ['./alert-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AlertListComponent implements OnChanges {
  private alertService = inject(AlertService);
  private router = inject(Router);
  private changeDetector = inject(ChangeDetectorRef);

  @Input() alerts: AlertItem[] | null = [];
  /** In the popup the list has a title and a link to the alerts page. */
  @Input() mode: 'popup' | 'page' = 'popup';
  @Input() tab: AlertTab = 'all';
  @Input() unreadOnly = false;
  @Output() alertClicked = new EventEmitter<void>();
  /** The tab or the unread filter changed, for the URL of the alerts page. */
  @Output() filterChanged = new EventEmitter<{
    tab: AlertTab;
    unreadOnly: boolean;
  }>();

  public readonly tabs: { key: AlertTab; label: string }[] = [
    { key: 'all', label: 'alerts.tab.all' },
    { key: 'company', label: 'alerts.tab.company' },
    { key: 'list', label: 'alerts.tab.list' },
  ];

  public counts: Record<AlertTab, number> = { all: 0, company: 0, list: 0 };
  public visibleAlerts: AlertItem[] = [];
  /** The rendered alerts of the visible ones. */
  public readonly rows = new IncrementalRows();
  public unreadVisible = 0;
  /** The alerts whose read state is being saved. */
  public saving = new Set<string>();

  public readonly target = alertTarget;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tab'] || changes['unreadOnly']) {
      this.rows.reset();
    }
    // New alerts, e.g. after one was marked read, keep the rendered rows.
    if (changes['alerts'] || changes['tab'] || changes['unreadOnly']) {
      this.filterAlerts();
    }
  }

  public selectTab(tab: AlertTab) {
    if (this.tab !== tab) {
      this.tab = tab;
      this.rows.reset();
      this.filterAlerts();
      this.filterChanged.emit({ tab, unreadOnly: this.unreadOnly });
    }
  }

  public setUnreadOnly(unreadOnly: boolean) {
    this.unreadOnly = unreadOnly;
    this.rows.reset();
    this.filterAlerts();
    this.filterChanged.emit({ tab: this.tab, unreadOnly });
  }

  public filterAlerts() {
    const alerts = (this.alerts ?? []).filter(
      (alert) => !this.unreadOnly || alert.unread
    );
    this.counts = {
      all: alerts.length,
      company: alerts.filter((x) => x.type === 'companyAlert').length,
      list: alerts.filter((x) => x.type === 'listAlert').length,
    };
    this.visibleAlerts = alerts.filter(
      (alert) =>
        this.tab === 'all' ||
        (this.tab === 'company' && alert.type === 'companyAlert') ||
        (this.tab === 'list' && alert.type === 'listAlert')
    );
    this.unreadVisible = this.visibleAlerts.filter((x) => x.unread).length;
  }

  public markAllAsRead = () => {
    this.updateAlerts(this.visibleAlerts.filter((x) => x.unread)).subscribe();
  };

  /**
   * Opens the target of an alert in the app; with a modifier key the browser
   * opens the link itself, e.g. in a new tab. The read state stays.
   */
  public openAlert(alert: AlertItem, event?: MouseEvent) {
    if (
      event &&
      (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0)
    ) {
      return;
    }
    event?.preventDefault();
    this.router.navigateByUrl(alertTarget(alert));
    this.alertClicked.emit();
  }

  /** Marks an alert read, or unread again. */
  public toggleRead(alert: AlertItem) {
    this.updateAlerts([alert]).subscribe();
  }

  private updateAlerts(alerts: AlertItem[]) {
    alerts.forEach((alert) => this.saving.add(alert.alert_id));
    return forkJoin(
      alerts.map((alert) => this.alertService.toggleAlert(alert))
    ).pipe(
      finalize(() => {
        alerts.forEach((alert) => this.saving.delete(alert.alert_id));
        this.alertService.updateAlerts();
        this.changeDetector.markForCheck();
      })
    );
  }
}
